import { runtimeConfig } from '@/shared/config'

// Зон в календаре две, и они отвечают на разные вопросы.
//
// Зона ОРГАНИЗАЦИИ (APP_TZ) — календарные сутки: «какое сегодня число»,
// границы недели и месяца, событие на весь день, период выборки. Она обязана
// быть одна на всех, иначе у людей разъезжаются границы общего дня.
//
// Зона МОМЕНТА — зона ЭТОГО устройства. Момент («когда начнётся») принадлежит
// тому, кто его читает и вводит: организация, растянутая на несколько зон,
// набирает «15:00» в разных смыслах, и прочитать эту строку зоной организации
// значит записать не тот момент. Устройство едет за человеком само — сохранённая
// в профиле зона отставала бы от перелёта молча.
//
// Инвариант модуля: Date — всегда МОМЕНТ (UTC внутри), а не «настенное время».
// Календарные поля (год/месяц/день/часы) получаются только через zonedParts с
// явной зоной, обратный переход — через zonedTime. Прямые getFullYear/getHours
// читают зону устройства неявно и в календарной математике запрещены.

export const APP_TIME_ZONE = runtimeConfig.APP_TZ

export interface ZonedParts {
  year: number
  /** 1–12 (как в календаре, не как в Date). */
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

// Intl-форматтеры дороги в создании, а зовутся сотнями раз на рендер сетки.
const partsFormatters = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = partsFormatters.get(timeZone)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    partsFormatters.set(timeZone, fmt)
  }
  return fmt
}

/** Календарные поля момента в зоне `timeZone`. */
export function zonedParts(instant: Date, timeZone: string = APP_TIME_ZONE): ZonedParts {
  const parts = partsFormatter(timeZone).formatToParts(instant)
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type)
    return found ? Number(found.value) : 0
  }
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  }
}

/** Смещение зоны от UTC в конкретный момент, в миллисекундах. */
export function zoneOffsetMs(instant: Date, timeZone: string = APP_TIME_ZONE): number {
  const p = zonedParts(instant, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  // Миллисекунды Intl не отдаёт — снимаем их с самого момента, иначе смещение
  // «дрожит» на долях секунды.
  return asUtc - (instant.getTime() - instant.getUTCMilliseconds())
}

/**
 * Момент по настенному времени зоны. Два прохода нужны из-за перевода стрелок:
 * смещение зависит от самого момента, и первое приближение может попасть в
 * соседний режим зоны. Несуществующее время (час, который зона пропустила)
 * нормализуется вперёд — это первая существующая точка.
 */
export function zonedTime(
  parts: {
    year: number
    month: number
    day: number
    hour?: number
    minute?: number
    second?: number
  },
  timeZone: string = APP_TIME_ZONE,
): Date {
  const wallUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  )
  const first = zoneOffsetMs(new Date(wallUtc), timeZone)
  const candidate = wallUtc - first
  const second = zoneOffsetMs(new Date(candidate), timeZone)
  return new Date(first === second ? candidate : wallUtc - second)
}

/** Зона самого устройства, как её видит Intl. Пустая — среда её не знает. */
export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Зона момента — зона устройства, и это вся логика. Спрашивать её больше негде
 * и не у кого: человек летает, а устройство летит с ним. Среда без Intl-зоны
 * (редкая) падает на зону организации — показать момент важнее, чем угадать.
 */
export function momentZone(): string {
  return deviceTimeZone() || APP_TIME_ZONE
}

/**
 * Подпись зоны для бейджа: `Москва · UTC+3`. Имя даёт сама зона (у зоны без
 * человеческого имени остаётся только офсет), офсет считается на конкретный
 * момент, потому что летом он другой.
 */
export function zoneLabel(timeZone: string = momentZone(), at: Date = new Date()): string {
  const offset = zoneOffsetLabel(timeZone, at)
  const short = zoneShortName(timeZone, at)
  return short && short !== offset ? `${short} · ${offset}` : offset
}

/**
 * Короткий тег зоны рядом со значением: `Москва`, а где у зоны нет
 * человеческого имени — её смещение (`UTC+3`). Хардкода имени зоны в UI нет:
 * тег выводится из самой зоны через Intl, поэтому в командировке он меняется
 * вместе с устройством и не начинает врать.
 */
export function zoneShortLabel(timeZone: string = momentZone(), at: Date = new Date()): string {
  return zoneShortName(timeZone, at) || zoneOffsetLabel(timeZone, at)
}

/**
 * Полная форма подписи зоны — одна формулировка на весь продукт: короткий тег
 * («Москва») расшифровывается везде одинаково. По умолчанию момент показан в
 * зоне СМОТРЯЩЕГО, то есть его устройства; `owner` нужен единственному экрану,
 * где смотрящего рядом с моментом нет, — публичной брони, которая показывает
 * чужое рабочее время в зоне владельца ссылки.
 */
export function zoneTitle(
  timeZone: string = momentZone(),
  scope: 'device' | 'owner' = 'device',
): string {
  const where = scope === 'owner' ? 'зоне владельца ссылки' : 'зоне вашего устройства'
  return `Время показано в ${where} (${timeZone})`
}

/** `UTC+3` / `UTC−5:30` / `UTC` — смещение зоны в момент `at`. */
export function zoneOffsetLabel(timeZone: string = APP_TIME_ZONE, at: Date = new Date()): string {
  const minutes = Math.round(zoneOffsetMs(at, timeZone) / 60_000)
  if (minutes === 0) return 'UTC'
  const sign = minutes > 0 ? '+' : '−'
  const abs = Math.abs(minutes)
  const hours = Math.floor(abs / 60)
  const rest = abs % 60
  return `UTC${sign}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`
}

const shortNameFormatters = new Map<string, Intl.DateTimeFormat>()

/**
 * Человеческое имя зоны («Москва») — из `longGeneric`, обрезанного по первой
 * запятой.
 *
 * НЕ `shortGeneric`, хотя он и выглядит короче и точнее: движки расходятся —
 * Node отдаёт «Москва», а Chromium «Moscow» латиницей посреди русского
 * интерфейса (проверено в живом браузере, юнит-тесты под Node этого не ловят).
 * `longGeneric` локализован в обоих, и его первая часть — ровно то короткое имя,
 * которое нужно. Зоны без человеческого имени отдают своё же смещение
 * («GMT+00:00») — тогда подписи имени нет, и остаётся наш офсет.
 */
function zoneShortName(timeZone: string, at: Date): string {
  let fmt = shortNameFormatters.get(timeZone)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('ru-RU', { timeZone, timeZoneName: 'longGeneric' })
    shortNameFormatters.set(timeZone, fmt)
  }
  const part = fmt.formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? ''
  const name = part.split(',')[0]?.trim() ?? ''
  return /^(GMT|UTC)/i.test(name) ? '' : name
}
